import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BillingData {
  subscription?: {
    plan: string;
    status: string;
    currentPeriodEnd: number;
  };
  paymentMethods: Array<{
    id: string;
    type: string;
    last4: string;
    isDefault: boolean;
  }>;
  invoices: Array<{
    id: string;
    amount: number;
    status: string;
    date: number;
  }>;
}

function Billing() {
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManageSubOpen, setIsManageSubOpen] = useState(false);
  const [isAddMethodOpen, setIsAddMethodOpen] = useState(false);
  const [isRemoveMethodOpen, setIsRemoveMethodOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('FREE');
  const [methodToRemove, setMethodToRemove] = useState<string | null>(null);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    nameOnCard: '',
  });

  useEffect(() => {
    fetchBilling();
  }, []);

  const fetchBilling = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/billing`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBilling(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch billing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openManageSubscription = () => {
    if (billing?.subscription) {
      setSelectedPlan(billing.subscription.plan);
      setIsManageSubOpen(true);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/billing/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan: selectedPlan }),
      });

      if (response.ok) {
        setIsManageSubOpen(false);
        fetchBilling();
      }
    } catch (error) {
      console.error('Failed to update subscription:', error);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/billing/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newPaymentMethod),
      });

      if (response.ok) {
        setIsAddMethodOpen(false);
        setNewPaymentMethod({ cardNumber: '', expiryDate: '', cvv: '', nameOnCard: '' });
        fetchBilling();
      }
    } catch (error) {
      console.error('Failed to add payment method:', error);
    }
  };

  const openRemoveDialog = (methodId: string) => {
    setMethodToRemove(methodId);
    setIsRemoveMethodOpen(true);
  };

  const handleRemovePaymentMethod = async () => {
    if (!methodToRemove) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/billing/payment-methods/${methodToRemove}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsRemoveMethodOpen(false);
        setMethodToRemove(null);
        fetchBilling();
      }
    } catch (error) {
      console.error('Failed to remove payment method:', error);
    }
  };

  const handleDownloadInvoice = (invoice: { id: string; amount: number; date: number; status: string }) => {
    const invoiceData = `
Invoice #${invoice.id}
Amount: $${invoice.amount.toFixed(2)}
Date: ${new Date(invoice.date * 1000).toLocaleDateString()}
Status: ${invoice.status}
    `;

    const blob = new Blob([invoiceData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoice.id}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CreditCard className="h-8 w-8" />
            Billing
          </h2>
          <p className="text-muted-foreground mt-2">
            Billing and subscription management
          </p>
        </div>
      </div>

      {billing?.subscription && (
        <Card>
          <CardHeader>
            <CardTitle>Current Subscription</CardTitle>
            <CardDescription>Your plan and billing information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Plan</p>
                <p className="text-2xl font-bold">{billing.subscription.plan}</p>
              </div>
              <Badge className="bg-green-500">{billing.subscription.status}</Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Period Ends</p>
              <p className="font-medium">
                {new Date(billing.subscription.currentPeriodEnd * 1000).toLocaleDateString()}
              </p>
            </div>
            <Button onClick={openManageSubscription}>Manage Subscription</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Payment Methods</CardTitle>
              <CardDescription>
                {billing?.paymentMethods?.length || 0} payment method
                {billing?.paymentMethods?.length !== 1 ? 's' : ''} on file
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => setIsAddMethodOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Method
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {billing?.paymentMethods && billing.paymentMethods.length > 0 ? (
            billing.paymentMethods.map((method) => (
              <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <CreditCard className="h-6 w-6" />
                  <div>
                    <p className="font-medium">
                      {method.type} ****{method.last4}
                    </p>
                    {method.isDefault && (
                      <Badge variant="outline" className="mt-1">
                        Default
                      </Badge>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openRemoveDialog(method.id)}>
                  Remove
                </Button>
              </div>
            ))
          ) : (
            <p className="text-center text-muted-foreground">No payment methods</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>
            {billing?.invoices?.length || 0} invoice{billing?.invoices?.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {billing?.invoices && billing.invoices.length > 0 ? (
            billing.invoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">${invoice.amount.toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(invoice.date * 1000).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <Badge className={invoice.status === 'PAID' ? 'bg-green-500' : 'bg-red-500'}>
                    {invoice.status}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => handleDownloadInvoice(invoice)}>
                    Download
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-muted-foreground">No invoices</p>
          )}
        </CardContent>
      </Card>

      {/* Manage Subscription Dialog */}
      <Dialog open={isManageSubOpen} onOpenChange={setIsManageSubOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Subscription</DialogTitle>
            <DialogDescription>
              Change your subscription plan
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="plan">Plan</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FREE">Free</SelectItem>
                  <SelectItem value="PRO">Pro</SelectItem>
                  <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {billing?.subscription && (
              <div className="text-sm text-muted-foreground">
                <p>Current Plan: {billing.subscription.plan}</p>
                <p>Status: {billing.subscription.status}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageSubOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleManageSubscription}>
              Update Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Method Dialog */}
      <Dialog open={isAddMethodOpen} onOpenChange={setIsAddMethodOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Add a new payment method to your account
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="cardNumber">Card Number</Label>
              <Input
                id="cardNumber"
                value={newPaymentMethod.cardNumber}
                onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, cardNumber: e.target.value })}
                placeholder="1234 5678 9012 3456"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="expiryDate">Expiry Date (MM/YY)</Label>
              <Input
                id="expiryDate"
                value={newPaymentMethod.expiryDate}
                onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, expiryDate: e.target.value })}
                placeholder="12/25"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cvv">CVV</Label>
              <Input
                id="cvv"
                value={newPaymentMethod.cvv}
                onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, cvv: e.target.value })}
                placeholder="123"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="nameOnCard">Name on Card</Label>
              <Input
                id="nameOnCard"
                value={newPaymentMethod.nameOnCard}
                onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, nameOnCard: e.target.value })}
                placeholder="John Doe"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddMethodOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddPaymentMethod}
              disabled={
                !newPaymentMethod.cardNumber ||
                !newPaymentMethod.expiryDate ||
                !newPaymentMethod.cvv ||
                !newPaymentMethod.nameOnCard
              }
            >
              Add Payment Method
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Payment Method Dialog */}
      <Dialog open={isRemoveMethodOpen} onOpenChange={setIsRemoveMethodOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Payment Method</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this payment method?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. This will permanently remove the payment method from your account.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRemoveMethodOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemovePaymentMethod}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Billing;
